import { ArrowLeft, Clock, Euro, Calendar, MapPin, Star, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import MessageModal from "@/components/MessageModal";
import EmbeddedMap from "@/components/EmbeddedMap";
import NavigationButtons from "@/components/NavigationButtons";
import { toast } from "sonner";

const ServiceDetail = () => {
  const navigate = useNavigate();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  
  // Mock service data - will be replaced with real data from props/API
  const service = {
    id: "1",
    title: "Marketing Strategy Consulting",
    shortDescription: "Boost je bedrijf met professionele marketing strategieën en groeiplannen op maat. Perfect voor ambitious women on a mission die hun business naar het volgende level willen brengen.",
    price: 125,
    duration: "90 min",
    availability: "Ma-Wo-Do tussen 10:00-16:00",
    rating: 4.9,
    reviewCount: 34,
    image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800",
    serviceType: "both" as const, // 'online' | 'in-person' | 'both'
    fullAddress: "Regentschapsstraat 58, 1000 Brussel, België",
    provider: {
      name: "Sarah van Dijk",
      avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200",
      location: "Brussel, België",
      verified: true
    },
    features: [
      "Persoonlijke 1-op-1 strategiesessie",
      "Uitgebreid marketing actieplan",
      "Follow-up resources en templates",
      "Online of op locatie mogelijk"
    ]
  };

  const handleViewAvailability = () => {
    console.log("Opening availability calendar");
    setCalendarOpen(true);
  };

  const handleBookingConfirmed = (date: Date, time: string) => {
    console.log("Booking confirmed:", { date, time });
    toast.success(`Afspraak bevestigd! Je ontvangt binnenkort een bevestigingsmail.`);
    // Here you would typically make an API call to create the booking
  };

  const handleSendMessage = () => {
    console.log("Opening message modal");
    setMessageModalOpen(true);
  };

  const handleMessageSent = (message: string) => {
    console.log("Message sent:", message);
    // Here you would typically make an API call to send the message
    // For now, we'll just redirect to messages page
    navigate('/berichten');
  };
  const handleGoBack = () => {
    console.log("Going back to previous screen");
    navigate(-1);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f1eb' }}>
      {/* Header with back button */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm" style={{ borderBottomColor: '#efe7dd', borderBottomWidth: '1px' }}>
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGoBack}
              className="flex items-center gap-2 px-4 py-2 font-medium hover:shadow-md transition-all"
              style={{ 
                backgroundColor: '#fbf8f4',
                borderColor: '#efe7dd',
                color: '#837069'
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Terug</span>
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold" style={{ color: '#837069' }}>
                Service Details
              </h1>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Ontdek wat deze woman on a mission te bieden heeft
              </p>
            </div>
          </div>

          {/* Service Header */}
          <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-2xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>Service Details</span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Bekijk alle informatie en boek direct een afspraak
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 pb-24">
        {/* Service Photo/Banner */}
        <div className="relative mb-6 rounded-2xl overflow-hidden shadow-lg">
          <img 
            src={service.image} 
            alt={service.title}
            className="w-full h-64 md:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </div>

        {/* Service Info Card */}
        <Card className="mb-6 shadow-sm" style={{ 
          borderColor: '#efe7dd', 
          backgroundColor: '#fbf8f4' 
        }}>
          <CardContent className="p-6">
            {/* Provider Info */}
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={service.provider.avatar} alt={service.provider.name} />
                <AvatarFallback style={{ backgroundColor: '#efe7dd', color: '#a0897c' }}>
                  {service.provider.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{service.provider.name}</h3>
                  {service.provider.verified && (
                    <Badge variant="secondary" className="text-xs" style={{ 
                      backgroundColor: '#efe7dd', 
                      color: '#a0897c' 
                    }}>
                      Verified
                    </Badge>
                  )}
                </div>
                <p className="text-sm flex items-center gap-1" style={{ color: '#b6a092' }}>
                  <MapPin className="h-3 w-3" />
                  {service.provider.location}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium" style={{ color: '#a0897c' }}>{service.rating}</span>
                <span className="text-xs" style={{ color: '#b6a092' }}>({service.reviewCount})</span>
              </div>
            </div>

            {/* Service Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {service.title}
            </h2>

            {/* Short Description */}
            <p className="leading-relaxed mb-6" style={{ color: '#a0897c' }}>
              {service.shortDescription}
            </p>

            {/* Service Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Price */}
              <div className="rounded-xl p-4" style={{ 
                backgroundColor: '#fbf8f4', 
                borderColor: '#efe7dd',
                border: '1px solid'
              }}>
                <div className="flex items-center gap-2 mb-1">
                  <Euro className="h-4 w-4" style={{ color: '#b6a092' }} />
                  <span className="text-sm font-medium" style={{ color: '#a0897c' }}>Prijs</span>
                </div>
                <p className="text-xl font-bold" style={{ color: '#837069' }}>€{service.price}</p>
                <p className="text-xs" style={{ color: '#b6a092' }}>per uur</p>
              </div>

              {/* Duration */}
              <div className="rounded-xl p-4" style={{ 
                backgroundColor: '#fbf8f4', 
                borderColor: '#efe7dd',
                border: '1px solid'
              }}>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4" style={{ color: '#b6a092' }} />
                  <span className="text-sm font-medium" style={{ color: '#a0897c' }}>Duur</span>
                </div>
                <p className="text-xl font-bold" style={{ color: '#837069' }}>{service.duration}</p>
                <p className="text-xs" style={{ color: '#b6a092' }}>sessie lengte</p>
              </div>
            </div>

            {/* Availability - Now with interactive button */}
            <div className="rounded-xl p-4 mb-6" style={{ 
              backgroundColor: '#fbf8f4', 
              borderColor: '#efe7dd',
              border: '1px solid'
            }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" style={{ color: '#b6a092' }} />
                  <span className="text-sm font-medium" style={{ color: '#a0897c' }}>Beschikbaarheid</span>
                </div>
                <Button 
                  onClick={handleViewAvailability}
                  size="sm"
                  className="text-white text-xs px-3 py-1"
                  style={{ backgroundColor: '#b6a092' }}
                >
                  Bekijk Agenda
                </Button>
              </div>
              <p className="font-medium" style={{ color: '#a0897c' }}>{service.availability}</p>
              <p className="text-xs mt-1" style={{ color: '#b6a092' }}>Klik op "Bekijk Agenda" voor exacte tijden</p>
            </div>

            {/* Features List */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Wat krijg je:</h4>
              <div className="space-y-2">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#b6a092' }} />
                    <span className="text-sm" style={{ color: '#a0897c' }}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Woman on a Mission Badge */}
            <div className="rounded-lg p-3 mb-6" style={{ 
              backgroundColor: '#efe7dd', 
              borderColor: '#e6dbd0',
              border: '1px solid'
            }}>
              <p className="font-medium text-sm flex items-center gap-2" style={{ color: '#837069' }}>
                <div className="text-white px-2 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#a0897c' }}>
                  WOM
                </div>
                Woman on a Mission - Professional Service
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Embedded Map - Location & Navigation */}
        <EmbeddedMap
          location={service.provider.location}
          address={service.fullAddress}
          serviceType={service.serviceType}
          providerName={service.provider.name}
        />
        {/* Action Buttons */}
        <NavigationButtons
          location={service.provider.location}
          address={service.fullAddress}
          serviceType={service.serviceType}
        />
      </div>

      {/* Availability Calendar Popup */}
      <AvailabilityCalendar
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onSelectSlot={handleBookingConfirmed}
      />

      {/* Message Modal */}
      <MessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        recipient={{
          name: service.provider.name,
          avatar: service.provider.avatar,
          verified: service.provider.verified
        }}
        subject={{
          title: service.title,
          type: 'service',
          price: service.price,
          image: service.image
        }}
        onSendMessage={handleMessageSent}
      />
    </div>
  );
};

export default ServiceDetail;